import { useEffect, useRef, useState } from "react";
import {
  formatWeekLabel,
  getWeekKey,
  loadHighscores,
  loadLastName,
  saveHighscore,
  saveLastName,
  type HighscoreEntry,
} from "../game/storage";
import { CATEGORIES, DIFFICULTY_LABELS, winnerOf, type GameConfig, type RoundResult } from "../game/types";

import { FireworksCanvas } from "./FireworksCanvas";

interface ResultsProps {
  config: GameConfig;
  p1: RoundResult;
  p2?: RoundResult;
  onRematch: () => void;
  onMenu: () => void;
}

function soloInsertRank(score: number, list: HighscoreEntry[]): number {
  let rank = list.length;
  for (let i = 0; i < list.length; i++) {
    if (score > (list[i]?.score ?? -Infinity)) {
      rank = i;
      break;
    }
  }
  return rank;
}

export function Results({ config, p1, p2, onRematch, onMenu }: ResultsProps) {
  const [highscores, setHighscores] = useState<HighscoreEntry[]>([]);
  const [savedDate, setSavedDate] = useState<string | null>(null);
  const [entryName, setEntryName] = useState(loadLastName() || "Player 1");
  const autoSavedRef = useRef(false);

  const catSymbol = CATEGORIES.find(c => c.id === config.selectedCategory.categoryId)?.symbol ?? "";
  const diffEmoji = DIFFICULTY_LABELS[config.selectedCategory.difficulty]?.emoji ?? "";

  useEffect(() => {
    if (config.mode === "solo" && !autoSavedRef.current) {
      autoSavedRef.current = true;
      const { categoryId, difficulty } = config.selectedCategory;
      loadHighscores(categoryId, difficulty).then(async (list) => {
        const rank = soloInsertRank(p1.score, list);
        if (rank < 10) {
          const date = new Date().toISOString();
          const name = p1.name.trim() || "Player 1";
          saveLastName(name);
          const updated = await saveHighscore({
            categoryId,
            difficulty,
            name,
            score: p1.score,
            correctCount: p1.correctCount,
            wrongCount: p1.wrongCount,
            date,
          });
          setHighscores(updated);
          setSavedDate(date);
        } else {
          setHighscores(list);
        }
      });
    }
  }, [config.mode, config.selectedCategory, p1]);

  if (config.mode === "duel" && p2) {
    const winner = winnerOf(p1, p2);
    const isDraw = winner === null;
    return (
      <div className="results">
        <h1 className="title">{isDraw ? "It's a draw!" : `${winner} wins!`}</h1>
        <p className="subtitle">60 seconds match · +2 for correct · -1 for wrong</p>

        <div className="duel-result-cards">
          <div className={`result-card ${!isDraw && winner === p1.name ? "winner" : ""}`}>
            <span className="result-name">{p1.name}</span>
            <span className="result-score">{p1.score} <small style={{ fontSize: "1rem" }}>pts</small></span>
            <div className="score-breakdown">
              <span className="correct-tag">✅ ตอบถูก {p1.correctCount}</span>
              <span className="wrong-tag">❌ ตอบผิด {p1.wrongCount}</span>
            </div>
          </div>
          <div className="versus">vs</div>
          <div className={`result-card ${!isDraw && winner === p2.name ? "winner" : ""}`}>
            <span className="result-name">{p2.name}</span>
            <span className="result-score">{p2.score} <small style={{ fontSize: "1rem" }}>pts</small></span>
            <div className="score-breakdown">
              <span className="correct-tag">✅ ตอบถูก {p2.correctCount}</span>
              <span className="wrong-tag">❌ ตอบผิด {p2.wrongCount}</span>
            </div>
          </div>
        </div>

        <div className="results-actions">
          <button type="button" className="btn secondary" onClick={onMenu}>
            Menu
          </button>
          <button type="button" className="btn" onClick={onRematch}>
            Rematch
          </button>
        </div>
      </div>
    );
  }

  const savedEntry = savedDate
    ? highscores.find(h => h.date === savedDate)
    : undefined;
  const rank = savedEntry
    ? highscores.indexOf(savedEntry)
    : soloInsertRank(p1.score, highscores);
  const qualifies = rank < 10 && savedEntry === undefined;

  const save = async () => {
    const name = entryName.trim() || "Player 1";
    const date = new Date().toISOString();
    saveLastName(name);
    setSavedDate(date);
    const updated = await saveHighscore({
      categoryId: config.selectedCategory.categoryId,
      difficulty: config.selectedCategory.difficulty,
      name,
      score: p1.score,
      correctCount: p1.correctCount,
      wrongCount: p1.wrongCount,
      date,
    });
    setHighscores(updated);
  };

  const isNewBest = savedEntry !== undefined && rank === 0;
  const isTop10 = rank < 10 || savedEntry !== undefined;

  return (
    <div className="results">
      {isTop10 && <FireworksCanvas />}
      <h1 className="title">Time's up!</h1>
      <p className="subtitle">{p1.name}'s Round Summary ({catSymbol} · {diffEmoji})</p>

      <div className="score-summary-card">
        <div className="final-score">{p1.score}</div>
        <span className="final-score-label">Total Score</span>

        <div className="stats-breakdown-row">
          <div className="stat-pill correct">
            <span className="stat-pill-label">✅ ตอบถูก</span>
            <span className="stat-pill-val">+{p1.correctCount * 2} ({p1.correctCount} ข้อ)</span>
          </div>
          <div className="stat-pill wrong">
            <span className="stat-pill-label">❌ ตอบผิด</span>
            <span className="stat-pill-val">{p1.wrongCount * -1} ({p1.wrongCount} ข้อ)</span>
          </div>
        </div>
      </div>

      <p className="subtitle">
        {isNewBest
          ? `🎉 New #1 Highscore! (${catSymbol} · ${diffEmoji})`
          : savedEntry
            ? `✨ Ranked #${rank + 1} on Top 10 (${catSymbol} · ${diffEmoji})`
            : "Play again to reach Top 10!"}
      </p>

      {qualifies && (
        <div className="hs-entry">
          <input
            className="name-input"
            value={entryName}
            onChange={e => setEntryName(e.target.value)}
            maxLength={16}
            placeholder="Your name"
            aria-label="Name for the highscore board"
          />
          <button type="button" className="btn" onClick={save}>
            Save to Top 10
          </button>
        </div>
      )}

      {highscores.length > 0 && (
        <div className="records-panel">
          <h2 className="panel-title">
            Top 10 Highscores ({catSymbol} · {diffEmoji}) · {formatWeekLabel(getWeekKey())}
          </h2>
          <ol className="hs-table solo-table">
            {highscores.map((h, i) => (
              <li key={i} className={`hs-row ${savedEntry && h.date === savedEntry.date ? "just-saved" : ""}`}>
                <span className="hs-rank">{i + 1}</span>
                <span className="hs-name">{h.name}</span>
                <span className="hs-details">
                  (ถูก {h.correctCount ?? 0} / ผิด {h.wrongCount ?? 0})
                </span>
                <span className="hs-score">{h.score} pts</span>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="results-actions">
        <button type="button" className="btn secondary" onClick={onMenu}>
          Menu
        </button>
        <button type="button" className="btn" onClick={onRematch}>
          Play again
        </button>
      </div>
    </div>
  );
}
